import { useEffect, useState } from "react";

/**
 * Shared partner-brand header used across mobile-capture / KYC dialogs.
 *
 * Rendering priority:
 *  1. If `product.capture_image_url` is set → full-width banner (3:1).
 *  2. Else → hero brand section (centred): logo tile + provider name +
 *     product title + subtitle line, on a wide brand-tinted band that
 *     spans the whole dialog width. This makes the dialog feel like the
 *     partner's own page.
 *  3. Favicon 404 fallback → coloured initials tile ("CS", "RF").
 *
 * All colour math derives from `product.brand_color` (hex). If empty,
 * we fall back to the site's default blue (`#0055FF`).
 */

export const DEFAULT_BRAND_COLOR = "#0055FF";
export const SAFE_BUTTON_COLOR = "#111111"; // used when brand colour is red-dominant

export function hostnameOf(url) {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) {
    // Split camelCase brand names ("CreditSea" → "CS").
    const camel = parts[0].match(/[A-Z][a-z]*/g);
    if (camel && camel.length >= 2) return (camel[0][0] + camel[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function normalizeColor(c) {
  const s = String(c || "").trim();
  if (!s) return DEFAULT_BRAND_COLOR;
  return s.startsWith("#") ? s.toUpperCase() : `#${s.toUpperCase()}`;
}

function rgbOf(hex) {
  const h = normalizeColor(hex).replace("#", "");
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function withAlpha(hex, alpha) {
  const c = rgbOf(hex);
  if (!c) return `rgba(0,85,255,${alpha})`;
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}

/** True when the brand colour is red-dominant (e.g. Poonawalla `#E31E24`).
 *  Product policy: never paint CTA buttons red — reads as an error/danger
 *  cue and hurts click-through. Callers should fall back to a safe colour.
 */
export function isRedDominant(hex) {
  const c = rgbOf(hex);
  if (!c) return false;
  return c.r > 170 && c.g < 100 && c.b < 100 && (c.r - c.g) > 80 && (c.r - c.b) > 80;
}

/** Colour we actually use for CTA buttons. Never red. */
export function safeButtonColor(hex) {
  const normalized = normalizeColor(hex);
  return isRedDominant(normalized) ? SAFE_BUTTON_COLOR : normalized;
}

export default function BrandHeader({ product }) {
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => { setLogoBroken(false); }, [product?.id, product?.redirect_url]);

  if (!product) return null;

  const captureImg = (product.capture_image_url || "").trim();
  const brandColor = normalizeColor(product.brand_color);
  const providerName = product.provider || product.title || "";
  const productTitle = (product.title && product.title !== providerName) ? product.title : "";
  const detailsLine = product.subtitle || "";
  const host = hostnameOf(product.redirect_url);
  const faviconUrl = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=256` : "";

  // 1) Admin-uploaded custom banner.
  if (captureImg) {
    return (
      <div
        className="w-full overflow-hidden -mx-6 -mt-6 mb-4 border-b"
        style={{ borderColor: withAlpha(brandColor, 0.15) }}
        data-testid="brand-header-banner"
      >
        <div style={{ aspectRatio: "3 / 1", background: withAlpha(brandColor, 0.06) }}>
          <img
            src={captureImg}
            alt={providerName}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      </div>
    );
  }

  // 2) Hero brand section — feels like the partner's own page.
  const showInitials = !faviconUrl || logoBroken;
  return (
    <div
      className="-mx-6 -mt-6 mb-4 px-6 py-6 border-b flex flex-col items-center text-center"
      style={{
        background: `linear-gradient(180deg, ${withAlpha(brandColor, 0.12)} 0%, ${withAlpha(brandColor, 0.03)} 100%)`,
        borderColor: withAlpha(brandColor, 0.18),
      }}
      data-testid="brand-header-hero"
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-sm mb-3"
        style={{
          background: showInitials ? brandColor : "white",
          border: `1px solid ${withAlpha(brandColor, 0.28)}`,
        }}
      >
        {showInitials ? (
          <span
            className="font-display text-lg font-bold text-white tracking-tight"
            data-testid="brand-header-initials"
          >
            {initialsOf(providerName)}
          </span>
        ) : (
          <img
            src={faviconUrl}
            alt={providerName}
            className="w-10 h-10 object-contain"
            onError={() => setLogoBroken(true)}
            data-testid="brand-header-logo"
          />
        )}
      </div>
      <div
        className="font-display text-xl font-bold tracking-tight leading-tight"
        style={{ color: brandColor }}
        data-testid="brand-header-name"
      >
        {providerName}
      </div>
      {productTitle && (
        <div
          className="text-[13px] text-neutral-800 font-medium mt-1"
          data-testid="brand-header-product-title"
        >
          {productTitle}
        </div>
      )}
      {detailsLine && (
        <div
          className="text-[12px] text-neutral-600 mt-1.5 max-w-[90%] leading-snug"
          data-testid="brand-header-details"
        >
          {detailsLine}
        </div>
      )}
    </div>
  );
}
